import { memo } from 'react';

import { useTranslation } from 'next-export-i18n';

import Currency from '@/components/common/currency';
import { TipModeEnum } from '@/types';
import { Box, Chip, Typography } from '@mui/material';

import styles from './styles.module.scss';
import { usePaymentLink } from '@/src/hooks';

type Props = {
  index: number;
  value: number;
  mostCommonTip?: number;
  selectedTip: number | undefined;
  handleSelectTip: (arg: number) => void;
  mode?: TipModeEnum;
  remaining: string;
};

const TipItem = memo(function TipItem({
  index,
  value,
  mostCommonTip,
  selectedTip,
  handleSelectTip,
  mode,
  remaining,
}: Props) {
  const { t } = useTranslation();
  const { data: payment } = usePaymentLink();
  const getContent = () => {
    if (
      mode === TipModeEnum.Percentage ||
      mode === TipModeEnum.PercentageWithHint
    ) {
      return (
        <>
          <Typography variant='body_md'>
            {value === 0 ? t('not-now') : `${value}%`}
          </Typography>
          {mode === TipModeEnum.PercentageWithHint &&
            (value === 0 ? null : (
              <>
                <Currency variant='currency-mini' ignoreForex />
                <Typography display='flex' gap='3px' variant='currency'>
                  <Typography variant='currency'>
                    {(Number(remaining) * (value / 100)).toFixed(
                      payment?.currencyPrecision,
                    )}
                  </Typography>
                </Typography>
              </>
            ))}
        </>
      );
    } else {
      return (
        <>
          {value !== 0 && <Currency variant='currency' ignoreForex />}
          <Typography variant='title_md'>
            {value === 0
              ? t('not-now')
              : value.toFixed(payment?.currencyPrecision)}
          </Typography>
          {mode === TipModeEnum.FixedWithHint && value !== 0 && (
            <Typography variant='caption'>{`(${Math.ceil(
              (value * 100) / Number(remaining),
            )}%)`}</Typography>
          )}
        </>
      );
    }
  };

  return (
    <Box display='flex' flexDirection='column' alignItems='center' gap='4px'>
      {mostCommonTip && mostCommonTip === value && (
        <Typography variant='caption' color='primary'>
          Most common
        </Typography>
      )}
      <Chip
        key={index}
        label={
          <Box display='flex' alignItems='baseline' gap='6px'>
            {getContent()}
          </Box>
        }
        variant={selectedTip === value ? 'filled' : 'outlined'}
        color={selectedTip === value ? 'primary' : 'secondary'}
        onClick={() => handleSelectTip(value)}
        className={styles.chip}
      />
    </Box>
  );
});

export default TipItem;
